import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getTaskCounts } from '../api/tasks';
import { useCurrentUser } from './useCurrentUser';

const BADGE_PREF_KEY = 'valentine-show-task-badge';
const REFRESH_INTERVAL_MS = 15 * 60 * 1000;
// Fix-batch item 3 (Task-6 review): 654 useMutation sites all fire the same
// MutationCache subscription below. A bulk op (e.g. clearing 30 tasks) fires
// N near-simultaneous "success" events, each of which was triggering its own
// counts fetch — a mutation storm of API calls for a badge that only needs
// the FINAL count. Debounce just this trigger path (trailing edge) so a
// burst collapses into one refresh; visibility/login/timer refreshes are
// untouched.
const MUTATION_DEBOUNCE_MS = 400;

/** localStorage per-device toggle default — MySettings (Task 7) writes this. */
export function getBadgePreference(): boolean {
  const raw = localStorage.getItem(BADGE_PREF_KEY);
  return raw === null ? true : raw === 'true';
}

export function setBadgePreference(enabled: boolean): void {
  localStorage.setItem(BADGE_PREF_KEY, String(enabled));
}

function tz(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

async function applyBadge(count: number) {
  if (typeof navigator === 'undefined') return;
  if (!getBadgePreference()) {
    // Fix-batch item 4: the preference-off early return used to skip
    // clearing too, so an OS badge already showing from BEFORE the user
    // flipped the toggle off would keep lingering (nothing ever refreshes
    // it again while the preference is off). Clear it defensively so
    // toggling off actually removes it.
    try {
      if (navigator.clearAppBadge) await navigator.clearAppBadge();
    } catch {
      // Feature-detected but the call itself can still reject on some
      // platforms (P3-A10) — never let a badge failure surface to the user.
    }
    return;
  }
  try {
    if (count > 0 && navigator.setAppBadge) {
      await navigator.setAppBadge(count);
    } else if (count === 0 && navigator.clearAppBadge) {
      await navigator.clearAppBadge();
    }
  } catch {
    // Feature-detected but the call itself can still reject on some
    // platforms (P3-A10) — never let a badge failure surface to the user.
  }
}

/** P3-A9/PP14: the icon badge's single source of truth.
 *
 * Refresh points:
 *  - mount (which doubles as "on login" — this effect's dependency array
 *    includes `user`, so it re-runs the instant `useCurrentUser` resolves
 *    from undefined to a real user, i.e. right after sign-in);
 *  - `visibilitychange` (tab/app foregrounded);
 *  - a CHAINED `setTimeout` roughly every 15 minutes — deliberately NOT
 *    `setInterval` (PP14): a backgrounded/sleeping phone doesn't run timers
 *    on schedule, so a plain interval either queues up a burst of missed
 *    ticks or fires repeatedly the instant the OS wakes the tab. Scheduling
 *    the NEXT timeout only after the current refresh settles avoids both;
 *  - any SUCCESSFUL mutation, observed via the **MutationCache** (PP14) —
 *    the QueryCache's own 'updated'/invalidation events do not fire for a
 *    query with no mounted observer, so a page that isn't showing the task
 *    board can't rely on watching specific query keys for a task
 *    create/complete/reassign happening elsewhere (Task Center, the AI Hub
 *    kanban, the Dashboard's my-work panel). Any mutation succeeding is
 *    cheap enough to treat as "something might have changed, go check" —
 *    this hook only READS caches, it never calls invalidateQueries itself,
 *    so it never touches Hub/Dashboard files (P3-A9's own constraint).
 */
export function useAppBadge(): void {
  const queryClient = useQueryClient();
  const { data: user } = useCurrentUser();

  useEffect(() => {
    if (!user) return;

    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    let mutationDebounceId: ReturnType<typeof setTimeout> | undefined;

    const refresh = async () => {
      try {
        const counts = await getTaskCounts(tz());
        if (!cancelled) await applyBadge(counts.overdue + counts.today);
      } catch {
        // Network hiccup — leave the badge as it was; the next refresh point retries.
      }
    };

    const scheduleNext = () => {
      if (cancelled) return;
      timeoutId = setTimeout(() => {
        void refresh().finally(scheduleNext);
      }, REFRESH_INTERVAL_MS);
    };

    void refresh();
    scheduleNext();

    const onVisible = () => {
      if (document.visibilityState === 'visible') void refresh();
    };
    document.addEventListener('visibilitychange', onVisible);

    const unsubscribe = queryClient.getMutationCache().subscribe((event) => {
      if (event.type === 'updated' && event.mutation.state.status === 'success') {
        // Trailing debounce (fix-batch item 3): a bulk op fires this
        // callback once per mutation, all within milliseconds of each
        // other. Collapse a burst into a single refresh, timed from the
        // LAST success rather than the first.
        if (mutationDebounceId) clearTimeout(mutationDebounceId);
        mutationDebounceId = setTimeout(() => {
          mutationDebounceId = undefined;
          void refresh();
        }, MUTATION_DEBOUNCE_MS);
      }
    });

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
      if (timeoutId) clearTimeout(timeoutId);
      if (mutationDebounceId) clearTimeout(mutationDebounceId);
      unsubscribe();
    };
  }, [user, queryClient]);
}
